// src/pages/ProductDetail.tsx
import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { FaStar } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import {
  FacebookShareButton,
  FacebookIcon,
  TwitterShareButton,
  TwitterIcon,
} from 'react-share';

const FaStarComponent = FaStar as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

interface Product {
  id: number;
  name: string;
  description: string;
  image: string;
}

const ProductDetail: React.FC = () => {
  const location = useLocation();
  const product = location.state?.product as Product;

  const [activeTab, setActiveTab] = useState<'description' | 'reviews'>(
    'description'
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Share URL
  const shareUrl = window.location.href;
  const productName = product?.name ?? 'Check out this product';
  const hashtag = `#${productName.replace(/\s+/g, '')}`; // e.g. #LaserPerforatedTippingPaper

  if (!product) {
    return (
      <div className='min-h-screen bg-[#FFFDF8] flex items-center justify-center'>
        <div className='text-center'>
          <h2 className='text-2xl font-semibold text-gray-800 mb-4'>
            Product Not Found
          </h2>
          <Link
            to='/products'
            className='text-[#021847] underline hover:text-[#E63946]'
          >
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-[#FFFDF8] py-12 px-4 sm:px-6 lg:px-8'>
      <div className='max-w-7xl mx-auto' style={{ paddingTop: '7rem' }}>
        {/* Breadcrumb */}
        <div className='text-md breadcrumbs mb-6'>
          <ul className='flex space-x-2 text-gray-600'>
            <li>
              <Link to='/' className='hover:text-[#021847]'>
                Home
              </Link>
            </li>
            <li>›</li>
            <li>
              <Link to='/products' className='hover:text-[#021847]'>
                Products
              </Link>
            </li>
            <li>›</li>
            <li className='text-[#021847]'>{product.name}</li>
          </ul>
        </div>

        <div className='relative py-10 px-4 sm:px-6 lg:px-8'>
          <div className='absolute top-0 left-0'>
            <h2 className='text-[#021847] text-2xl font-semibold uppercase tracking-widest'>
              SHOP
            </h2>
            <div className='w-12 h-0.5 bg-[#021847] mt-1' />
          </div>
        </div>

        <div className='grid grid-cols-1 lg:grid-cols-2 gap-12'>
          {/* Image */}
          <div className='bg-white rounded-lg shadow-md overflow-hidden'>
            <img
              src={product.image}
              alt={product.name}
              className='w-full h-auto object-cover max-h-[500px]'
              onError={(e) => (e.currentTarget.src = '/placeholder.jpg')}
            />
          </div>

          {/* Info */}
          <div className='flex flex-col'>
            <h1 className='text-3xl md:text-4xl font-bold text-[#021847] mb-4 uppercase'>
              {product.name}
            </h1>

            <div className='flex items-center space-x-4 mb-6'>
              <span className='text-sm text-gray-600'>
                CATEGORY: <span className='uppercase'>{product.name}</span>
              </span>
            </div>

            {/* SHARE BUTTONS – FIXED */}
            <div className='flex items-center space-x-4 mb-8'>
              <span className='text-sm text-gray-600'>SHARE ON:</span>

              <div className='flex space-x-2'>
                {/* Facebook – uses hashtag only */}
                <FacebookShareButton
                  url={shareUrl}
                  hashtag={shareUrl} // This appears in the post
                  className='w-8 h-8 rounded-full overflow-hidden'
                >
                  <FacebookIcon
                    size={32}
                    round
                    className='hover:brightness-90 transition'
                  />
                </FacebookShareButton>

                {/* Twitter – uses title */}
                <TwitterShareButton
                  url={shareUrl}
                  title={productName}
                  className='w-8 h-8 rounded-full overflow-hidden'
                >
                  <TwitterIcon
                    size={32}
                    round
                    className='hover:brightness-90 transition'
                  />
                </TwitterShareButton>
              </div>
            </div>

            {/* Tabs */}
            <div className='border-b border-gray-300 mb-6'>
              <div className='flex space-x-8'>
                <button
                  onClick={() => setActiveTab('description')}
                  className={`pb-2 px-1 text-sm font-medium transition ${activeTab === 'description'
                      ? 'border-b-2 border-[#021847] text-[#021847]'
                      : 'text-gray-600 hover:text-[#021847]'
                    }`}
                >
                  DESCRIBE
                </button>
                <button
                  onClick={() => setActiveTab('reviews')}
                  className={`pb-2 px-1 text-sm font-medium transition relative ${activeTab === 'reviews'
                      ? 'border-b-2 border-[#021847] text-[#021847]'
                      : 'text-gray-600 hover:text-[#021847]'
                    }`}
                >
                  USER REVIEWS (0)
                </button>
              </div>
            </div>

            {/* Tab Content */}
            <div className='flex-grow'>
              {activeTab === 'description' ? (
                <div className='prose prose-lg max-w-none text-gray-700'>
                  <p>{product.description}</p>
                  <p className='mt-4'>
                    Our {product.name.toLowerCase()} is crafted with precision
                    to meet the highest industry standards.
                  </p>
                </div>
              ) : (
                <div className='text-center py-12 text-gray-500'>
                  <FaStarComponent
                    className='mx-auto text-yellow-400 mb-2'
                    size={32}
                  />
                  <p>No reviews yet.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
